import type { SeedsMapEntryInput } from "@/types/creativesphere";
import type { IngestedProductSchema } from "@/types/ingestedProduct";

// Must match the product contract keys documented on SeedMessage.params in seed.proto.
export const PRODUCT_SEED_CONTRACT_KEYS = [
  "product_name",
  "product_image_url",
  "description",
  "product_category",
  "physical_integrity_statement",
  "required_visibility",
  "forbidden_alterations",
] as const;

type ProductSeedContractKey = (typeof PRODUCT_SEED_CONTRACT_KEYS)[number];

export interface PilotProductSeedInput {
  title: string;
  image_url: string;
  description?: string;
  category?: string;
}

export interface ProductDetails {
  description?: string;
  category?: string;
}

function nonEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

// Inline pilot product seed; codecs/product.ts is the GenLab library seed and is not interchangeable.
export function productSeedMessage(
  input: PilotProductSeedInput,
): SeedsMapEntryInput {
  const contract: Partial<Record<ProductSeedContractKey, string>> = {};
  const put = (key: ProductSeedContractKey, value?: string) => {
    const v = nonEmpty(value);
    if (v) contract[key] = v;
  };
  put("product_name", input.title);
  put("product_image_url", input.image_url);
  put("description", input.description);
  put("product_category", input.category);
  return {
    seed_type: "product",
    name: nonEmpty(input.title) ?? "",
    params: { contract },
  };
}

export function productDetailsFromIngested(
  product: Pick<
    IngestedProductSchema,
    "description" | "short_description" | "categories"
  >,
): ProductDetails {
  const description =
    nonEmpty(product.short_description) ?? nonEmpty(product.description);
  const category = nonEmpty(product.categories?.[0]);
  return {
    ...(description ? { description } : {}),
    ...(category ? { category } : {}),
  };
}
